/* Fouroz design system behaviour (Claude Design handoff, Oct 2026). Plain JS, no dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 01 Announcement bar: fade between messages every 4s */
  function initAnnouncement(root) {
    if (root.dataset.fzReady) return;
    root.dataset.fzReady = '1';
    const msgs = [...root.querySelectorAll('.fz-announcement__msg')];
    if (msgs.length < 2 || reduceMotion) return;
    let i = 0;
    setInterval(() => {
      if (root.matches(':hover')) return;
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    }, (Number(root.dataset.interval) || 4) * 1000);
  }

  /* Menu drawer: open/close, scrim, Esc, scroll lock, focus trap, focus return */
  function initDrawer(drawer) {
    if (drawer.dataset.fzReady) return;
    drawer.dataset.fzReady = '1';
    const openers = document.querySelectorAll(`[aria-controls="${drawer.id}"]`);
    let lastFocus = null;
    const focusables = () => [...drawer.querySelectorAll('a[href], button, summary, [tabindex]:not([tabindex="-1"])')].filter(el => el.offsetParent !== null);

    const open = () => {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      document.documentElement.classList.add('fz-lock');
      openers.forEach(b => b.setAttribute('aria-expanded', 'true'));
      const f = focusables();
      (f[0] || drawer).focus();
    };
    const close = () => {
      drawer.hidden = true;
      document.documentElement.classList.remove('fz-lock');
      openers.forEach(b => b.setAttribute('aria-expanded', 'false'));
      if (lastFocus) lastFocus.focus();
    };
    openers.forEach(b => b.addEventListener('click', e => { e.preventDefault(); open(); }));
    drawer.querySelectorAll('[data-fz-drawer-close]').forEach(el => el.addEventListener('click', close));
    drawer.addEventListener('keydown', e => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* 03 Hero: scroll-snap slideshow with dots, autoplay, pause/resume */
  function initHero(root) {
    if (root.dataset.fzReady) return;
    root.dataset.fzReady = '1';
    const track = root.querySelector('[data-fz-hero-track]');
    const slides = [...track.children];
    const dots = [...root.querySelectorAll('.fz-hero__dot')];
    if (slides.length < 2) return;
    let current = 0, paused = false, resumeT;
    const setDot = n => dots.forEach((d, i) => d.setAttribute('aria-current', i === n ? 'true' : 'false'));
    const go = n => {
      current = n;
      track.scrollTo({ left: n * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      setDot(n);
    };
    const pause = () => {
      paused = true;
      clearTimeout(resumeT);
      resumeT = setTimeout(() => { paused = false; }, 8000);
    };
    track.addEventListener('scroll', () => {
      const n = Math.round(track.scrollLeft / track.clientWidth);
      if (n !== current) { current = n; setDot(n); }
    }, { passive: true });
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('mouseenter', () => { paused = true; clearTimeout(resumeT); });
    track.addEventListener('mouseleave', () => { paused = false; });
    root.addEventListener('focusin', pause);
    dots.forEach((d, i) => d.addEventListener('click', () => { pause(); go(i); }));
    if (root.dataset.autoplay === 'true' && !reduceMotion) {
      setInterval(() => {
        if (paused || document.hidden) return;
        go((current + 1) % slides.length);
      }, (Number(root.dataset.interval) || 5) * 1000);
    }
    // Theme editor: jump to the selected slide
    root.addEventListener('shopify:block:select', e => {
      const i = slides.indexOf(e.target);
      if (i > -1) { paused = true; go(i); }
    });
  }

  /* 05 Best sellers tabs */
  function initTabs(root) {
    if (root.dataset.fzReady) return;
    root.dataset.fzReady = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const viewAll = root.querySelector('[data-fz-view-all]');
    const select = (tab, focus) => {
      tabs.forEach(t => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = root.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (viewAll && tab.dataset.viewAll) viewAll.href = tab.dataset.viewAll;
      if (focus) tab.focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(t));
      t.addEventListener('keydown', e => {
        let n = null;
        if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') n = 0;
        if (e.key === 'End') n = tabs.length - 1;
        if (n !== null) { e.preventDefault(); select(tabs[n], true); }
      });
    });
    root.addEventListener('shopify:block:select', e => {
      const t = tabs.find(x => x.closest('[data-block-id]') === e.target || x.dataset.blockId === e.target.dataset.blockId);
      if (t) select(t);
    });
  }

  /* Cart helpers shared by quick add and the header badge */
  function toast(msg) {
    const el = document.createElement('div');
    el.className = 'fz-toast';
    el.setAttribute('role', 'status');
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  }
  async function refreshCartCount() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      document.querySelectorAll('[data-fz-cart-count]').forEach(b => {
        b.textContent = cart.item_count;
        b.hidden = cart.item_count === 0;
      });
      return cart;
    } catch (e) { return null; }
  }
  document.addEventListener('cart:change', refreshCartCount);
  document.addEventListener('cart:updated', refreshCartCount);

  // The header bag opens Prestige's cart drawer when it exists, otherwise goes to /cart
  document.addEventListener('click', e => {
    const link = e.target.closest('[data-fz-cart-link]');
    if (!link) return;
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.show === 'function') { e.preventDefault(); drawer.show(); }
  });

  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-fz-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (!btn.dataset.variantId) { window.location.href = btn.dataset.productUrl; return; }
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag');
      const cart = await refreshCartCount();
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1500);
      // Let Prestige re-render and open its cart drawer
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.show === 'function') setTimeout(() => drawer.show(), 150);
      else toast('Added to your bag');
    } catch (err) {
      toast(err.message || 'Could not add to bag. Please try again.');
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  /* 12 Newsletter: submitting state */
  function initNewsletter(form) {
    if (form.dataset.fzReady) return;
    form.dataset.fzReady = '1';
    form.addEventListener('submit', () => {
      const b = form.querySelector('button[type="submit"]');
      if (b) { b.disabled = true; b.textContent = b.dataset.loading || 'Joining…'; }
    });
  }

  /* 13 Footer: columns expanded on wide screens */
  function initFooter(root) {
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => root.querySelectorAll('details').forEach(d => { d.open = mq.matches; });
    apply();
    if (!root.dataset.fzReady) { root.dataset.fzReady = '1'; mq.addEventListener('change', apply); }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-fz-announcement]').forEach(initAnnouncement);
    scope.querySelectorAll('[data-fz-drawer]').forEach(initDrawer);
    scope.querySelectorAll('[data-fz-hero]').forEach(initHero);
    scope.querySelectorAll('[data-fz-tabs]').forEach(initTabs);
    scope.querySelectorAll('.fz-newsletter__form').forEach(initNewsletter);
    scope.querySelectorAll('[data-fz-footer]').forEach(initFooter);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
